import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../hooks/useAuthStore';
import {
  ArrowLeft, Phone, Mail, MapPin, Calendar, MessageSquare, User,
  AlertTriangle, Send, CheckCircle, RotateCcw, Clock, Video, MapPinned,
  FileText, Utensils,
} from 'lucide-react';
import clsx from 'clsx';

const PHASE_NAMES = ['Onboarding', 'Valoración', 'Rastreo', 'Ejecución', 'Seguimiento'];

interface CandidateDetail {
  candidate: {
    id: string; first_name: string | null; last_name: string | null; whatsapp_name: string | null; whatsapp_phone: string;
    email: string | null; country: string | null; city: string | null; current_phase: number; phase_status: string;
    status: string; created_at: string; last_interaction: string; assigned_minister_id: string | null;
    minister_notes: string | null; internal_notes: string | null;
  };
  responses: Array<{
    id: string; question_text: string; response_text: string; phase_number: number;
    created_at: string; category?: string | null;
  }>;
  progress: {
    totalQuestions: number; answeredQuestions: number; currentPhase: number;
    phases: Array<{ phase: number; status: string; progress: number; }>;
  };
  candidatePhases?: Array<{
    id: string; phase_number: number; status: string;
    fasting_start_date: string | null; fasting_end_date: string | null; fasting_confirmed: number;
    fasting_current_day: number; fasting_day1_completed_at: string | null;
    fasting_day2_completed_at: string | null; fasting_day3_completed_at: string | null;
    fasting_started_at_time: string | null;
    session_scheduled_at: string | null; session_type: string | null; session_completed: number;
    review_notes: string | null;
  }>;
}

interface ConversationMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  message_type: string;
  message_content: string;
  status: string;
  created_at: string;
}

interface ConversationsResponse {
  conversations: ConversationMessage[];
  total: number;
  page: number;
  limit: number;
}

export function CandidateDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState<CandidateDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'info' | 'responses' | 'timeline'>('info');

  // Timeline state
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Approval state
  const [approving, setApproving] = useState(false);
  const [revisionMessage, setRevisionMessage] = useState('');
  const [showRevisionInput, setShowRevisionInput] = useState(false);

  // Fasting/session approval state
  const [approvingFasting, setApprovingFasting] = useState(false);
  const [completingSession, setCompletingSession] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Note state
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Minister assignment state
  const [ministers, setMinisters] = useState<Array<{ id: string; first_name: string; last_name: string }>>([]);
  const [assigningMinister, setAssigningMinister] = useState(false);

  const fetchMinisters = useCallback(async () => {
    const result = await apiRequest<{ ministers: Array<{ id: string; first_name: string; last_name: string }> }>('/ministers');
    if (result.success && result.data) {
      setMinisters(result.data.ministers || []);
    }
  }, []);

  const handleAssignMinister = async (ministerId: string) => {
    if (!id) return;
    setAssigningMinister(true);
    const result = await apiRequest(`/candidates/${id}/assign-minister`, {
      method: 'POST',
      body: JSON.stringify({ ministerId }),
    });
    if (result.success) {
      await fetchCandidate(true);
    }
    setAssigningMinister(false);
  };

  const fetchCandidate = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    const result = await apiRequest<CandidateDetail>(`/candidates/${id}`);
    if (result.success && result.data) setData(result.data);
    if (!silent) setIsLoading(false);
  }, [id]);

  useEffect(() => {
    fetchCandidate();
    fetchMinisters();
  }, [fetchCandidate, fetchMinisters]);

  const fetchMessages = useCallback(async () => {
    if (!id) return;
    setMessagesLoading(true);
    const result = await apiRequest<ConversationsResponse>(`/candidates/${id}/conversations?limit=100`);
    if (result.success && result.data) {
      setMessages(result.data.conversations);
    }
    setMessagesLoading(false);
  }, [id]);

  useEffect(() => {
    if (activeTab === 'timeline') {
      fetchMessages();
    }
  }, [activeTab, fetchMessages]);

  useEffect(() => {
    if (activeTab === 'timeline') {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !id) return;
    setSendingMessage(true);
    const result = await apiRequest(`/candidates/${id}/send-message`, {
      method: 'POST',
      body: JSON.stringify({ message: newMessage.trim() }),
    });
    if (result.success) {
      setNewMessage('');
      await fetchMessages();
    }
    setSendingMessage(false);
  };

  const handleApprovePhase = async () => {
    if (!id) return;
    setApproving(true);
    setValidationError('');
    const result = await apiRequest(`/candidates/${id}/approve-phase`, { method: 'POST' });
    if (result.success) {
      await fetchCandidate(true);
    } else if (result.error) {
      setValidationError(result.error);
    }
    setApproving(false);
  };

  const handleApproveFasting = async () => {
    if (!id) return;
    setApprovingFasting(true);
    setValidationError('');
    const result = await apiRequest(`/candidates/${id}/approve-fasting`, { method: 'POST' });
    if (result.success) {
      await fetchCandidate(true);
    } else if (result.error) {
      setValidationError(result.error);
    }
    setApprovingFasting(false);
  };

  const handleCompleteSession = async () => {
    if (!id) return;
    setCompletingSession(true);
    setValidationError('');
    const result = await apiRequest(`/candidates/${id}/complete-session`, { method: 'POST' });
    if (result.success) {
      await fetchCandidate(true);
    } else if (result.error) {
      setValidationError(result.error);
    }
    setCompletingSession(false);
  };

  const handleRequestRevision = async () => {
    if (!id || !revisionMessage.trim()) return;
    setApproving(true);
    const result = await apiRequest(`/candidates/${id}/request-revision`, {
      method: 'POST',
      body: JSON.stringify({ message: revisionMessage.trim() }),
    });
    if (result.success) {
      setRevisionMessage('');
      setShowRevisionInput(false);
      await fetchCandidate(true);
    }
    setApproving(false);
  };

  const handleAddNote = async () => {
    if (!id || !noteText.trim()) return;
    setSavingNote(true);
    const result = await apiRequest(`/candidates/${id}/add-note`, {
      method: 'POST',
      body: JSON.stringify({ note: noteText.trim(), noteType: 'minister_notes' }),
    });
    if (result.success) {
      setNoteText('');
      await fetchCandidate(true);
    }
    setSavingNote(false);
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  }

  if (!data) {
    return (
      <div className="card p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-warning-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Candidato no encontrado</h3>
        <Link to="/candidates" className="btn-primary mt-4">Volver a candidatos</Link>
      </div>
    );
  }

  const { candidate, responses, progress, candidatePhases } = data;
  const name = candidate.first_name ? `${candidate.first_name} ${candidate.last_name || ''}` : candidate.whatsapp_name || 'Sin nombre';

  const currentPhaseData = candidatePhases?.find(p => p.phase_number === candidate.current_phase);
  const phase1Data = candidatePhases?.find(p => p.phase_number === 1);
  const phase3Data = candidatePhases?.find(p => p.phase_number === 3);
  const isPendingReview = currentPhaseData?.status === 'pending_review' || candidate.phase_status === 'pending_review';

  // Group responses by phase
  const responsesByPhase: Record<number, typeof responses> = {};
  for (const r of responses) {
    if (!responsesByPhase[r.phase_number]) responsesByPhase[r.phase_number] = [];
    responsesByPhase[r.phase_number].push(r);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/candidates" className="btn-ghost p-2"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{name}</h1>
          <p className="text-gray-500 dark:text-gray-400">ID: {candidate.id}</p>
        </div>
        <span className={clsx('badge', candidate.status === 'active' ? 'badge-success' : candidate.status === 'paused' ? 'badge-warning' : 'badge-gray')}>
          {candidate.status === 'active' ? 'Activo' : candidate.status === 'paused' ? 'Pausado' : candidate.status}
        </span>
      </div>

      {/* Pending Review Banner */}
      {isPendingReview && (
        <div className="card p-4 border-l-4 border-warning-500 bg-warning-50 dark:bg-warning-500/10">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-warning-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold text-warning-800 dark:text-warning-300">
                Fase {candidate.current_phase} pendiente de revisión
              </h4>
              <p className="text-sm text-warning-700 dark:text-warning-400 mt-1">
                El candidato completó esta fase y espera aprobación para continuar.
              </p>
              <div className="flex gap-2 mt-3">
                <button onClick={handleApprovePhase} disabled={approving} className="btn-primary text-sm flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4" />
                  {approving ? 'Aprobando...' : 'Aprobar Fase'}
                </button>
                <button onClick={() => setShowRevisionInput(!showRevisionInput)} className="btn-ghost text-sm flex items-center gap-1.5">
                  <RotateCcw className="w-4 h-4" />
                  Solicitar Revisión
                </button>
              </div>
              {showRevisionInput && (
                <div className="mt-3 flex gap-2">
                  <input
                    type="text" value={revisionMessage} onChange={(e) => setRevisionMessage(e.target.value)}
                    placeholder="Mensaje para el candidato..."
                    className="input flex-1 text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleRequestRevision()}
                  />
                  <button onClick={handleRequestRevision} disabled={approving || !revisionMessage.trim()} className="btn-primary text-sm">
                    Enviar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Validation Error Alert */}
      {validationError && (
        <div className="card p-4 border-l-4 border-danger-500 bg-danger-50 dark:bg-danger-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-danger-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold text-danger-800 dark:text-danger-300">Error de Validación</h4>
              <p className="text-sm text-danger-700 dark:text-danger-400 mt-1">{validationError}</p>
              <button onClick={() => setValidationError('')} className="text-xs text-danger-600 underline mt-2">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Card */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Progreso del Proceso</h3>
        <div className="flex items-center gap-2 mb-4">
          {progress.phases.map((p, i) => (
            <div key={i} className="flex-1">
              <div className={clsx(
                'h-2 rounded-full',
                p.status === 'completed' ? 'bg-success-500' :
                p.status === 'in_progress' ? 'bg-primary-500' :
                p.status === 'pending_review' ? 'bg-warning-500' :
                'bg-gray-200 dark:bg-dark-600'
              )} />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">{PHASE_NAMES[p.phase]}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">Fase actual: <strong>{PHASE_NAMES[candidate.current_phase]}</strong></span>
          <span className="text-gray-600 dark:text-gray-400">Preguntas: <strong>{progress.answeredQuestions}/{progress.totalQuestions}</strong></span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-700">
        <nav className="flex gap-4">
          {(['info', 'responses', 'timeline'] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors', activeTab === tab ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400')}>
              {tab === 'info' ? 'Información' : tab === 'responses' ? 'Respuestas' : 'Historial'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact Info */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Datos de Contacto</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-gray-400" /><span className="text-gray-700 dark:text-gray-300">{candidate.whatsapp_phone}</span></div>
              {candidate.email && <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-gray-400" /><span className="text-gray-700 dark:text-gray-300">{candidate.email}</span></div>}
              {(candidate.city || candidate.country) && <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-gray-400" /><span className="text-gray-700 dark:text-gray-300">{[candidate.city, candidate.country].filter(Boolean).join(', ')}</span></div>}
              <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-gray-400" /><span className="text-gray-700 dark:text-gray-300">Registrado: {new Date(candidate.created_at).toLocaleDateString('es-CO')}</span></div>
              <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-gray-400" /><span className="text-gray-700 dark:text-gray-300">Última interacción: {new Date(candidate.last_interaction).toLocaleString('es-CO')}</span></div>
            </div>
          </div>

          {/* Minister */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Ministro Asignado</h3>
            {candidate.assigned_minister_id && (
              <div className="flex items-center gap-3 mb-4">
                <div className="avatar"><User className="w-5 h-5" /></div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {ministers.find(m => m.id === candidate.assigned_minister_id)
                      ? `${ministers.find(m => m.id === candidate.assigned_minister_id)!.first_name} ${ministers.find(m => m.id === candidate.assigned_minister_id)!.last_name}`
                      : 'Ministro'}
                  </p>
                  <p className="text-xs text-gray-500">ID: {candidate.assigned_minister_id.slice(0, 12)}...</p>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <select
                value={candidate.assigned_minister_id || ''}
                onChange={(e) => e.target.value && handleAssignMinister(e.target.value)}
                disabled={assigningMinister}
                className="input flex-1 text-sm"
              >
                <option value="">Seleccionar ministro...</option>
                {ministers.map((m) => (
                  <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                ))}
              </select>
              {assigningMinister && <div className="spinner self-center" />}
            </div>
          </div>

          {/* Fasting Info - Enhanced */}
          {phase1Data && (phase1Data.fasting_start_date || phase1Data.fasting_current_day > 0 || phase1Data.fasting_confirmed) && (
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Utensils className="w-5 h-5" /> Ayuno
              </h3>
              <div className="space-y-3">
                {/* Progress bar */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500 dark:text-gray-400">Progreso</span>
                    <span className="font-medium text-gray-900 dark:text-white">{phase1Data.fasting_current_day || 0}/3 días</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-3">
                    <div
                      className={clsx('h-3 rounded-full transition-all', phase1Data.fasting_confirmed ? 'bg-success-500' : 'bg-primary-500')}
                      style={{ width: `${((phase1Data.fasting_current_day || 0) / 3) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Status */}
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Estado:</span>
                  <span className={clsx('font-medium text-sm px-2 py-0.5 rounded-full', {
                    'bg-gray-100 text-gray-600 dark:bg-dark-600 dark:text-gray-400': !phase1Data.fasting_start_date,
                    'bg-primary-100 text-primary-700 dark:bg-primary-500/20 dark:text-primary-300': phase1Data.fasting_start_date && (phase1Data.fasting_current_day || 0) < 3 && !phase1Data.fasting_confirmed,
                    'bg-warning-100 text-warning-700 dark:bg-warning-500/20 dark:text-warning-300': (phase1Data.fasting_current_day || 0) >= 3 && !phase1Data.fasting_confirmed,
                    'bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-300': !!phase1Data.fasting_confirmed,
                  })}>
                    {!phase1Data.fasting_start_date ? 'No iniciado' :
                     phase1Data.fasting_confirmed ? 'Aprobado' :
                     (phase1Data.fasting_current_day || 0) >= 3 ? 'Completado (pendiente aprobación)' :
                     `En progreso (día ${phase1Data.fasting_current_day || 0}/3)`}
                  </span>
                </div>

                {phase1Data.fasting_start_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Inicio:</span>
                    <span className="text-gray-900 dark:text-white">{new Date(phase1Data.fasting_start_date).toLocaleDateString('es-CO')}</span>
                  </div>
                )}

                {/* Day timestamps */}
                {[phase1Data.fasting_day1_completed_at, phase1Data.fasting_day2_completed_at, phase1Data.fasting_day3_completed_at].map((ts, i) => (
                  ts && (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Día {i + 1}:</span>
                      <span className="text-success-600">
                        <CheckCircle className="w-3.5 h-3.5 inline mr-1" />
                        {new Date(ts).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </div>
                  )
                ))}

                {/* Approve button */}
                {(phase1Data.fasting_current_day || 0) >= 3 && !phase1Data.fasting_confirmed && (
                  <button
                    onClick={handleApproveFasting}
                    disabled={approvingFasting}
                    className="btn-primary w-full text-sm flex items-center justify-center gap-2 mt-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {approvingFasting ? 'Aprobando...' : 'Aprobar Ayuno'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Session Info - Enhanced */}
          {phase3Data?.session_scheduled_at && (
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                {phase3Data.session_type === 'virtual' ? <Video className="w-5 h-5" /> : <MapPinned className="w-5 h-5" />}
                Sesión de Liberación
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Fecha:</span>
                  <span className="text-gray-900 dark:text-white">
                    {new Date(phase3Data.session_scheduled_at).toLocaleString('es-CO')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Tipo:</span>
                  <span className="text-gray-900 dark:text-white capitalize">
                    {phase3Data.session_type === 'virtual' ? 'Virtual' : phase3Data.session_type === 'in_person' ? 'Presencial' : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Completada:</span>
                  <span className={clsx('font-medium', phase3Data.session_completed ? 'text-success-600' : 'text-warning-600')}>
                    {phase3Data.session_completed ? 'Sí' : 'Pendiente'}
                  </span>
                </div>

                {/* Complete session button */}
                {!phase3Data.session_completed && (
                  <button
                    onClick={handleCompleteSession}
                    disabled={completingSession}
                    className="btn-primary w-full text-sm flex items-center justify-center gap-2 mt-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {completingSession ? 'Completando...' : 'Marcar Sesión Completada'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Minister Notes */}
          <div className="card p-6 lg:col-span-2">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Notas del Ministro
            </h3>
            {candidate.minister_notes && (
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3 mb-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {candidate.minister_notes}
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                value={noteText} onChange={(e) => setNoteText(e.target.value)}
                placeholder="Agregar una nota..."
                className="input flex-1 text-sm resize-none"
                rows={2}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
              />
              <button onClick={handleAddNote} disabled={savingNote || !noteText.trim()} className="btn-primary text-sm self-end">
                {savingNote ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'responses' && (
        <div className="space-y-6">
          {/* Approval Panel at top if pending */}
          {isPendingReview && (
            <div className="card p-4 bg-warning-50 dark:bg-warning-500/10 border border-warning-200 dark:border-warning-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-warning-800 dark:text-warning-300">Revisión Requerida</h4>
                  <p className="text-sm text-warning-700 dark:text-warning-400">Revisa las respuestas y aprueba o solicita revisión.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleApprovePhase} disabled={approving} className="btn-primary text-sm flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4" /> Aprobar
                  </button>
                  <button onClick={() => setShowRevisionInput(!showRevisionInput)} className="btn-ghost text-sm flex items-center gap-1.5">
                    <RotateCcw className="w-4 h-4" /> Revisión
                  </button>
                </div>
              </div>
              {showRevisionInput && (
                <div className="mt-3 flex gap-2">
                  <input
                    type="text" value={revisionMessage} onChange={(e) => setRevisionMessage(e.target.value)}
                    placeholder="Escribe el motivo de la revisión..."
                    className="input flex-1 text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleRequestRevision()}
                  />
                  <button onClick={handleRequestRevision} disabled={approving || !revisionMessage.trim()} className="btn-primary text-sm">Enviar</button>
                </div>
              )}
            </div>
          )}

          {/* Responses grouped by phase */}
          {Object.keys(responsesByPhase).length === 0 ? (
            <div className="card p-12">
              <div className="empty-state"><MessageSquare className="empty-state-icon" /><h3 className="empty-state-title">Sin respuestas aún</h3></div>
            </div>
          ) : (
            Object.entries(responsesByPhase)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([phase, phaseResponses]) => (
                <div key={phase} className="card">
                  <div className="p-4 border-b border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-700/50">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      Fase {phase}: {PHASE_NAMES[Number(phase)]}
                    </h4>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-dark-700">
                    {phaseResponses.map((r) => (
                      <div key={r.id} className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            {r.category && (
                              <span className="inline-block text-xs bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300 rounded px-2 py-0.5 mb-1">
                                {r.category}
                              </span>
                            )}
                            <p className="font-medium text-gray-900 dark:text-white">{r.question_text}</p>
                            <p className="mt-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-dark-700 rounded-lg p-3">{r.response_text}</p>
                            <p className="text-xs text-gray-400 mt-1">{new Date(r.created_at).toLocaleString('es-CO')}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {activeTab === 'timeline' && (
        <div className="card flex flex-col" style={{ height: 'calc(100vh - 380px)', minHeight: '400px' }}>
          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messagesLoading ? (
              <div className="flex justify-center py-8"><div className="spinner" /></div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No hay mensajes aún</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className={clsx('flex', msg.direction === 'outbound' ? 'justify-end' : 'justify-start')}>
                  <div className={clsx(
                    'max-w-[75%] rounded-2xl px-4 py-2 text-sm',
                    msg.direction === 'outbound'
                      ? 'bg-primary-500 text-white rounded-br-md'
                      : 'bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white rounded-bl-md'
                  )}>
                    <p className="whitespace-pre-wrap break-words">{msg.message_content}</p>
                    <div className={clsx(
                      'flex items-center gap-1 mt-1 text-xs',
                      msg.direction === 'outbound' ? 'text-primary-200 justify-end' : 'text-gray-400'
                    )}>
                      <span>{new Date(msg.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</span>
                      {msg.direction === 'outbound' && (
                        <span>{msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓✓' : msg.status === 'sent' ? '✓' : msg.status === 'failed' ? '✗' : '⏳'}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Message input */}
          <div className="border-t border-gray-200 dark:border-dark-700 p-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                placeholder="Escribe un mensaje..."
                className="input flex-1"
                disabled={sendingMessage}
              />
              <button
                onClick={handleSendMessage}
                disabled={sendingMessage || !newMessage.trim()}
                className="btn-primary p-2.5"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
